import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc,
  query, where,
} from 'firebase/firestore';
import { db } from './firebase';
import { filterGroupsByAccess } from './utils/locationAccess';
import {
  Clock, MapPin, LogIn, LogOut, Timer, ChevronDown,
  CheckCircle2, XCircle, Camera, StopCircle, History,
  Pencil, Trash2, X, Save,
} from 'lucide-react';
import LoadingSpinner from './components/LoadingSpinner';

export default function TimeClock({ user, userData, companyId }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [todayRecords, setTodayRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clocking, setClocking] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showScanner, setShowScanner] = useState(false);
  const [scanError, setScanError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  // Edit modal state
  const [editRecord, setEditRecord] = useState(null);
  const [editClockIn, setEditClockIn] = useState('');
  const [editClockOut, setEditClockOut] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const accessibleGroups = filterGroupsByAccess(groups, userData);
  const isAdmin = userData?.role === 'admin';

  const today = new Date().toISOString().split('T')[0];

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Elapsed time for active session
  useEffect(() => {
    if (!activeSession) { setElapsed(0); return; }
    const tick = () => {
      const start = new Date(activeSession.clockIn).getTime();
      setElapsed(Math.floor((Date.now() - start) / 1000));
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [activeSession]);

  // Load groups
  useEffect(() => {
    if (!companyId) return;
    const unsub = onSnapshot(
      collection(db, 'companies', companyId, 'shiftsGroups'),
      (snap) => setGroups(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => console.error('Error loading groups:', err),
    );
    return unsub;
  }, [companyId]);

  // Auto-select group from QR URL param
  useEffect(() => {
    const groupParam = searchParams.get('group');
    if (groupParam && accessibleGroups.length > 0) {
      const found = accessibleGroups.find(g => g.id === groupParam);
      if (found) setSelectedGroup(found);
    }
  }, [searchParams, accessibleGroups]);

  useEffect(() => {
    if (!selectedGroup) return;
    if (!accessibleGroups.some((group) => group.id === selectedGroup.id)) {
      setSelectedGroup(null);
    }
  }, [accessibleGroups, selectedGroup]);

  // Listen to today's time records
  useEffect(() => {
    if (!companyId || !user) return;
    const constraints = [where('date', '==', today)];
    if (!isAdmin) {
      constraints.unshift(where('userId', '==', user.uid));
    }
    const q = query(
      collection(db, 'companies', companyId, 'timeRecords'),
      ...constraints,
    );
    const unsub = onSnapshot(q, (snap) => {
      const records = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => new Date(b.clockIn || 0).getTime() - new Date(a.clockIn || 0).getTime());
      setTodayRecords(records);
      // Find active session for the signed-in admin/staff member only
      const active = records.find(r => r.status === 'active' && r.userId === user.uid);
      setActiveSession(active || null);
      setLoading(false);
    }, (err) => {
      console.error('Error loading time records:', err);
      setLoading(false);
    });
    return unsub;
  }, [companyId, isAdmin, today, user]);

  // Format seconds to HH:MM:SS
  const formatDuration = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Format minutes to hours/minutes display
  const formatMinutes = (mins) => {
    if (!mins || mins < 1) return '0m';
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  // Clock In
  const handleClockIn = async () => {
    if (!selectedGroup) return;
    setClocking(true);
    try {
      const now = new Date().toISOString();
      await addDoc(collection(db, 'companies', companyId, 'timeRecords'), {
        userId: user.uid,
        staffName: userData?.displayName || userData?.username || user.email?.split('@')[0] || 'Unknown',
        groupId: selectedGroup.id,
        groupName: selectedGroup.name,
        groupColor: selectedGroup.color || '#2563EB',
        date: today,
        clockIn: now,
        clockOut: null,
        duration: null,
        status: 'active',
        companyId,
        createdAt: now,
      });
    } catch (err) {
      console.error('Clock in failed:', err);
      alert(t('timeClock.clockInFailed'));
    }
    setClocking(false);
  };

  // Clock Out
  const handleClockOut = async () => {
    if (!activeSession) return;
    setClocking(true);
    try {
      const now = new Date();
      const clockInTime = new Date(activeSession.clockIn);
      const durationMinutes = Math.round((now - clockInTime) / 60000);
      await updateDoc(doc(db, 'companies', companyId, 'timeRecords', activeSession.id), {
        clockOut: now.toISOString(),
        duration: durationMinutes,
        status: 'completed',
      });
    } catch (err) {
      console.error('Clock out failed:', err);
      alert(t('timeClock.clockOutFailed'));
    }
    setClocking(false);
  };

  // QR Scanner
  const stopScanner = useCallback(async () => {
    try {
      if (html5QrCodeRef.current) {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
      }
    } catch (err) {
      console.error('Error stopping scanner:', err);
    }
    setShowScanner(false);
  }, []);

  const startScanner = useCallback(async () => {
    setScanError('');
    setShowScanner(true);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      // Small delay to let the DOM render
      await new Promise(r => setTimeout(r, 300));
      const scanner = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          // Try to extract group ID from URL or raw text
          let groupId = decodedText;
          try {
            const url = new URL(decodedText);
            groupId = url.searchParams.get('group') || decodedText;
          } catch {
            // Not a URL, use raw text as groupId
          }
          const found = accessibleGroups.find(g => g.id === groupId);
          if (found) {
            setSelectedGroup(found);
            stopScanner();
          } else {
            setScanError(t('timeClock.invalidQR'));
          }
        },
        () => {}, // ignore scan failures
      );
    } catch (err) {
      console.error('Scanner error:', err);
      setScanError(t('timeClock.cameraError'));
      setShowScanner(false);
    }
  }, [groups, t, stopScanner]);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  // Open edit modal
  const openEditModal = (record) => {
    if (!isAdmin) return;
    setEditRecord(record);
    // Convert ISO to datetime-local format (YYYY-MM-DDTHH:MM)
    const toLocal = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setEditClockIn(toLocal(record.clockIn));
    setEditClockOut(toLocal(record.clockOut));
    setEditError('');
  };

  // Save edited record
  const handleEditSave = async () => {
    if (!isAdmin) return;
    if (!editRecord || !editClockIn) return;
    setEditSaving(true);
    setEditError('');
    try {
      const newClockIn = new Date(editClockIn);
      const newClockOut = editClockOut ? new Date(editClockOut) : null;

      if (newClockOut && newClockOut <= newClockIn) {
        setEditError('Clock out must be after clock in.');
        setEditSaving(false);
        return;
      }

      const updates = {
        clockIn: newClockIn.toISOString(),
        clockOut: newClockOut ? newClockOut.toISOString() : null,
        duration: newClockOut ? Math.round((newClockOut - newClockIn) / 60000) : null,
        status: newClockOut ? 'completed' : 'active',
        editedAt: new Date().toISOString(),
        editedBy: user.uid,
      };

      await updateDoc(doc(db, 'companies', companyId, 'timeRecords', editRecord.id), updates);
      setEditRecord(null);
    } catch (err) {
      console.error('Edit save failed:', err);
      setEditError(err.message || 'Failed to save changes.');
    }
    setEditSaving(false);
  };

  // Delete record
  const handleDeleteRecord = async () => {
    if (!isAdmin) return;
    if (!editRecord) return;
    if (!window.confirm('Are you sure you want to delete this time record? This cannot be undone.')) return;
    setEditSaving(true);
    try {
      await deleteDoc(doc(db, 'companies', companyId, 'timeRecords', editRecord.id));
      setEditRecord(null);
    } catch (err) {
      console.error('Delete failed:', err);
      setEditError(err.message || 'Failed to delete record.');
    }
    setEditSaving(false);
  };

  // Total hours today
  const totalMinutesToday = todayRecords.reduce((sum, r) => {
    if (r.duration) return sum + r.duration;
    if (r.status === 'active') {
      return sum + Math.round((Date.now() - new Date(r.clockIn).getTime()) / 60000);
    }
    return sum;
  }, 0);

  if (loading) return <LoadingSpinner />;

  const staffName = userData?.displayName || userData?.username || user?.email?.split('@')[0] || 'User';

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Clock size={24} className="text-primary" />
          {t('timeClock.title')}
        </h1>
        <p className="text-gray-400 mt-1">{t('timeClock.subtitle', { name: staffName })}</p>
      </div>

      {/* Live Clock */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 text-center">
        <p className="text-5xl font-mono font-bold text-white tracking-wider">
          {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </p>
        <p className="text-gray-400 mt-2 text-sm">
          {currentTime.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Active Session Banner */}
      {activeSession && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
            <div>
              <p className="text-green-400 font-semibold text-sm">{t('timeClock.clockedIn')}</p>
              <p className="text-white font-medium">{activeSession.groupName}</p>
              {activeSession.clockInLocation?.address && (
                <p className="text-gray-500 text-xs flex items-center gap-1 mt-0.5">
                  <MapPin size={10} /> {activeSession.clockInLocation.address}
                </p>
              )}
              <p className="text-gray-400 text-xs">
                {t('timeClock.since')} {new Date(activeSession.clockIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-mono font-bold text-green-400">{formatDuration(elapsed)}</p>
            <p className="text-xs text-gray-400">{t('timeClock.elapsed')}</p>
          </div>
        </div>
      )}

      {/* Location Selector + QR Scanner */}
      {!activeSession && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 space-y-4">
          <p className="text-sm font-medium text-gray-300">{t('timeClock.selectLocation')}</p>

          <div className="flex gap-2">
            {/* Dropdown */}
            <div className="relative flex-1">
              <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                value={selectedGroup?.id || ''}
                onChange={(e) => {
                  const g = accessibleGroups.find(g => g.id === e.target.value);
                  setSelectedGroup(g || null);
                }}
                className="w-full pl-9 pr-8 py-2.5 bg-white/5 border border-white/20 rounded-lg text-white appearance-none cursor-pointer focus:ring-2 focus:ring-primary/50 focus:border-primary"
              >
                <option value="" className="bg-slate-800 text-gray-300">{t('timeClock.chooseLocation')}</option>
                {accessibleGroups.map(g => (
                  <option key={g.id} value={g.id} className="bg-slate-800 text-white">{g.name}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* Scan QR button */}
            <button
              onClick={showScanner ? stopScanner : startScanner}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                showScanner
                  ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                  : 'bg-primary/20 text-primary border border-primary/30 hover:bg-primary/30'
              }`}
            >
              {showScanner ? <StopCircle size={16} /> : <Camera size={16} />}
              {showScanner ? t('timeClock.stopScan') : t('timeClock.scanQR')}
            </button>
          </div>

          {/* QR Scanner Area */}
          {showScanner && (
            <div className="space-y-2">
              <div
                id="qr-reader"
                ref={scannerRef}
                className="rounded-lg overflow-hidden border border-white/10"
                style={{ width: '100%' }}
              />
              {scanError && (
                <p className="text-red-400 text-sm flex items-center gap-1">
                  <XCircle size={14} /> {scanError}
                </p>
              )}
            </div>
          )}

          {/* Selected Group Display */}
          {selectedGroup && (
            <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                style={{ backgroundColor: selectedGroup.color || '#2563EB' }}
              >
                {(selectedGroup.name || '?')[0].toUpperCase()}
              </div>
              <div>
                <p className="text-primary font-medium text-sm">{selectedGroup.name}</p>
                {selectedGroup.address && (
                  <p className="text-gray-400 text-xs">{selectedGroup.address}</p>
                )}
              </div>
              <CheckCircle2 size={16} className="text-primary ml-auto" />
            </div>
          )}
        </div>
      )}

      {/* Clock In / Clock Out Button */}
      <div className="flex justify-center">
        {activeSession ? (
          <button
            onClick={handleClockOut}
            disabled={clocking}
            className="w-full max-w-sm py-5 rounded-2xl text-xl font-bold transition-all transform hover:scale-[1.02] active:scale-[0.98] bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            <LogOut size={28} />
            {clocking ? t('timeClock.clockingOut') : t('timeClock.clockOut')}
          </button>
        ) : (
          <button
            onClick={handleClockIn}
            disabled={clocking || !selectedGroup}
            className="w-full max-w-sm py-5 rounded-2xl text-xl font-bold transition-all transform hover:scale-[1.02] active:scale-[0.98] bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            <LogIn size={28} />
            {clocking ? t('timeClock.clockingIn') : t('timeClock.clockIn')}
          </button>
        )}
      </div>

      {/* Today's Summary */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <History size={20} className="text-primary" />
            {t('timeClock.todayRecords')}
          </h2>
          <div className="text-right">
            <p className="text-sm text-gray-400">{t('timeClock.totalToday')}</p>
            <p className="text-lg font-bold text-primary">{formatMinutes(totalMinutesToday)}</p>
          </div>
        </div>

        {todayRecords.length === 0 ? (
          <div className="text-center py-6">
            <Timer size={36} className="text-gray-600 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">{t('timeClock.noRecords')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {todayRecords.map(record => (
              <div
                key={record.id}
                className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-2 h-8 rounded-full"
                    style={{ backgroundColor: record.groupColor || '#2563EB' }}
                  />
                  <div>
                    <p className="text-sm font-medium text-white">{record.groupName}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <span>
                        {new Date(record.clockIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span>→</span>
                      <span>
                        {record.clockOut
                          ? new Date(record.clockOut).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : t('timeClock.inProgress')
                        }
                      </span>
                    </div>
                    {record.clockInLocation?.address && (
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                        <MapPin size={10} />
                        <span>{record.clockInLocation.address}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {record.status === 'active' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                      {t('timeClock.active')}
                    </span>
                  ) : (
                    <p className="text-sm font-medium text-gray-300">{formatMinutes(record.duration)}</p>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => openEditModal(record)}
                      className="p-1.5 rounded-lg hover:bg-white/10 text-gray-500 hover:text-primary transition-colors"
                      title="Edit record"
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Record Modal */}
      {editRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Pencil size={18} className="text-primary" />
                Edit Time Record
              </h3>
              <button
                onClick={() => setEditRecord(null)}
                className="p-1 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-gray-400">{editRecord.groupName}</p>
              <p className="text-xs text-gray-500">{editRecord.staffName || editRecord.userName || 'Unknown'}</p>
              {editRecord.editedAt && (
                <p className="text-xs text-amber-500/70">Last edited: {new Date(editRecord.editedAt).toLocaleString()}</p>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Clock In</label>
                <input
                  type="datetime-local"
                  value={editClockIn}
                  onChange={(e) => setEditClockIn(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Clock Out</label>
                <input
                  type="datetime-local"
                  value={editClockOut}
                  onChange={(e) => setEditClockOut(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
                {!editClockOut && (
                  <p className="text-xs text-gray-500 mt-1">Leave empty to keep session active (no clock out).</p>
                )}
              </div>
            </div>

            {editError && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-lg">
                {editError}
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={handleEditSave}
                disabled={editSaving || !editClockIn}
                className="flex-1 bg-primary hover:bg-primary/80 text-white py-2.5 rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Save size={16} />
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
              {isAdmin && (
                <button
                  onClick={handleDeleteRecord}
                  disabled={editSaving}
                  className="px-4 py-2.5 rounded-lg bg-red-600/20 text-red-400 hover:bg-red-600/30 border border-red-500/30 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
